import React, { type JSX } from 'react';

import type {} from '@mint-lib/theme/src/extensions/chip.js';
import type {} from '@mint-lib/theme/src/extensions/typography.js';

import {
  Box,
  Button,
  Chip,
  ChipProps,
  Icon,
  RichTooltip,
  Typography,
} from '@myn/mui';

import { type ApprovalFlowsStatusData } from '../../../schemas.js';
import type { InnerObject } from '../../../types.js';
import MoreDetailsModal from '../MoreDetailsModal/MoreDetailsModal.jsx';

type ButtonClickHandler = () => void;

export type StatusBarProps = {
  approvalFlowsStatusData?: ApprovalFlowsStatusData;
  width?: string;
  onClickSendForApproval?: () => void;
  onClickReject?: ButtonClickHandler;
  onClickApprove?: ButtonClickHandler;
  onClickWithdraw?: () => void;
  isDisabled?: boolean;
  innerObj: InnerObject;
};

interface StatusInfo {
  color: ChipProps['color'];
  label: string;
  buttons?: JSX.Element[];
}

const getLabelForApprover = (userDecision?: string | null): string => {
  switch (userDecision) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Pending';
  }
};
const getDraftStatusInfo = (
  onClickSendForApproval?: () => void,
  isDisabled?: boolean,
): StatusInfo => ({
  color: 'silver',
  label: 'Draft',
  buttons: [
    <Button
      key="sendApproval"
      color="secondary"
      variant="contained"
      startIcon="Send"
      onClick={onClickSendForApproval}
      disabled={isDisabled}
    >
      Send for approval
    </Button>,
  ],
});

const getProgressStatusInfo = (
  user: string,
  userDecision?: string | null,
  isDisabled?: boolean,
  onClickReject?: ButtonClickHandler,
  onClickApprove?: ButtonClickHandler,
  onClickWithdraw?: () => void,
): StatusInfo => {
  const label =
    user === 'approver' ? getLabelForApprover(userDecision) : 'Pending';
  const isDisabledCondition =
    userDecision === 'rejected' || userDecision === 'approved' || isDisabled;

  return {
    color: 'info',
    label: label,
    buttons:
      user === 'approver'
        ? [
            <Button
              key="reject"
              color="error"
              size="small"
              startIcon="CloseOutline"
              onClick={onClickReject}
              disabled={isDisabledCondition}
            >
              Reject
            </Button>,
            <Button
              key="approve"
              color="primary"
              size="small"
              startIcon="CheckmarkOutline"
              onClick={onClickApprove}
              disabled={isDisabledCondition}
            >
              Approve
            </Button>,
          ]
        : getRequestorButtons(user, onClickWithdraw, isDisabled),
  };
};

const getRequestorButtons = (
  user: string,
  onClickWithdraw?: () => void,
  isDisabled?: boolean,
): JSX.Element[] => {
  if (user !== 'requestor') {
    return [];
  }

  return [
    <Button
      key="cancel"
      color="primary"
      variant="outlined"
      startIcon="CloseOutline"
      onClick={onClickWithdraw}
      disabled={isDisabled}
    >
      Withdraw approval request
    </Button>,
  ];
};
const getRejectedStatusInfo = (
  user: string,
  onClickSendForApproval?: () => void,
  isDisabled?: boolean,
  onClickReject?: ButtonClickHandler,
  onClickApprove?: ButtonClickHandler,
): StatusInfo => ({
  color: 'error',
  label: 'Rejected',
  buttons: [
    ...(user === 'requestor'
      ? [
          <Button
            key="sendApproval"
            color="secondary"
            variant="contained"
            startIcon="Send"
            onClick={onClickSendForApproval}
            disabled={isDisabled}
          >
            Send for approval
          </Button>,
        ]
      : []),
    ...(user === 'approver'
      ? [
          <Button
            key="reject"
            color="error"
            size="small"
            startIcon="CloseOutline"
            onClick={onClickReject}
            disabled
          >
            Reject
          </Button>,
          <Button
            key="approve"
            color="primary"
            size="small"
            startIcon="CheckmarkOutline"
            onClick={onClickApprove}
            disabled
          >
            Approve
          </Button>,
        ]
      : []),
  ],
});

const getStatusInfo = (
  approvalFlowsStatusData: ApprovalFlowsStatusData,
  onClickSendForApproval?: () => void,
  onClickReject?: ButtonClickHandler,
  onClickApprove?: ButtonClickHandler,
  onClickWithdraw?: () => void,
  isDisabled?: boolean,
): StatusInfo | null => {
  const {
    userStatus: user,
    approvalItemStatus,
    userDecision,
  } = approvalFlowsStatusData;

  switch (approvalItemStatus) {
    case 'draft':
      return getDraftStatusInfo(onClickSendForApproval, isDisabled);
    case 'progress':
      return getProgressStatusInfo(
        user,
        userDecision,
        isDisabled,
        onClickReject,
        onClickApprove,
        onClickWithdraw,
      );
    case 'approved':
      return {
        color: 'success',
        label: 'Approved',
        buttons: [],
      };
    case 'rejected':
      return getRejectedStatusInfo(
        user,
        onClickSendForApproval,
        isDisabled,
        onClickReject,
        onClickApprove,
      );

    default:
      return null;
  }
};

export const StatusBar = ({
  width,
  onClickSendForApproval,
  onClickReject,
  onClickApprove,
  onClickWithdraw,
  isDisabled,
  approvalFlowsStatusData,
  innerObj,
}: StatusBarProps) => {
  const [moreDetailsDialogOpen, setMoreDetailsDialogOpen] =
    React.useState(false);

  if (!approvalFlowsStatusData) {
    return null;
  }

  const statusInfo = getStatusInfo(
    approvalFlowsStatusData,
    onClickSendForApproval,
    onClickReject,
    onClickApprove,
    onClickWithdraw,
    isDisabled,
  );

  if (!statusInfo) {
    return null;
  }

  const { color, label, buttons } = statusInfo;

  return (
    <Box
      sx={{
        display: 'flex',
        width: width ? width : '100%',
        gap: '12px',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <Typography variant="label01" color="text.secondary">
          Approval status:
        </Typography>
        {statusInfo.label !== 'Draft' ? (
          <>
            <RichTooltip
              title="Check the status details"
              placement="left"
              primaryButtonProps={{
                label: 'View details',
                onClick: () => {
                  setMoreDetailsDialogOpen(true);
                },
              }}
            >
              <Chip
                color={color}
                label={label}
                variant="outlined"
                icon={<Icon component="Information" />}
              />
            </RichTooltip>
            <MoreDetailsModal
              open={moreDetailsDialogOpen}
              onClose={() => setMoreDetailsDialogOpen(false)}
              innerObj={innerObj}
              approvalFlowsStatusData={approvalFlowsStatusData}
            />
          </>
        ) : (
          <Chip
            color={color}
            label={label}
            variant="outlined"
            icon={<Icon component="Information" />}
          />
        )}
      </Box>
      {buttons && (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {buttons.map((button) => button)}
        </Box>
      )}
    </Box>
  );
};

export default StatusBar;
